/**
 * Service for interacting with the Shyft API
 */
import { env } from '../config/env';

const SHYFT_API_URL = `https://rpc.shyft.to?api_key=${env.SHYFT_API_KEY}`;

// Solana RPC URL for direct Solana interactions - use Shyft instead of mainnet-beta
const SOLANA_RPC_URL = SHYFT_API_URL;

// Cache for getBlockHeight to avoid excessive API calls
let blockHeightCache: { timestamp: number; value: number | null } = {
  timestamp: 0,
  value: null
};

// Cache for token balances
let tokenBalancesCache: Record<string, { timestamp: number; value: any }> = {};

// Cache timeout in milliseconds (reduced from 5s to 2s for more frequent refreshes)
const CACHE_TIMEOUT = 2000;

// For transactions, we want an even shorter cache timeout
const TX_CACHE_TIMEOUT = 500;

// Add a new constant for post-transaction cache timeout
const POST_TX_CACHE_TIMEOUT = 100;

// Import these at the top of the file
import { Connection, Transaction, Keypair, PublicKey, SystemProgram, sendAndConfirmTransaction as solanaSignAndConfirmTransaction } from '@solana/web3.js';

/**
 * Safely converts values to JSON-serializable format
 * Prevents circular reference errors
 */
function sanitizeForStringify(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle simple types
  if (typeof obj !== 'object') {
    return obj;
  }

  // Special handling for PublicKey objects or similar wallet address objects
  if (obj &&
    typeof obj.toBase58 === 'function') {
    return obj.toBase58();
  }

  // If it's a DOM node or has toString method and is not a plain object, use string representation
  if ((typeof window !== 'undefined' && window.Node && obj instanceof window.Node) ||
    (typeof obj.toString === 'function' &&
      Object.prototype.toString.call(obj) !== '[object Object]')) {
    return obj.toString();
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForStringify(item));
  }

  // Handle plain objects
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) &&
      !key.startsWith('__react') &&
      !key.startsWith('_')) {
      try {
        result[key] = sanitizeForStringify(obj[key]);
      } catch (error) {
        result[key] = '[Circular or Unsupported]';
      }
    }
  }
  return result;
}

/**
 * Safely converts any address type to string
 */
function ensureAddressString(address: any): string {
  if (address === null || address === undefined) {
    throw new Error('Address cannot be null or undefined');
  }

  // Handle PublicKey and similar wallet objects with toBase58 method
  if (typeof address === 'object' && address !== null && typeof address.toBase58 === 'function') {
    return address.toBase58();
  }

  // If it's already a string, return it
  if (typeof address === 'string') {
    return address;
  }

  // If it's an object with toString method, use that
  if (typeof address === 'object' && address !== null && typeof address.toString === 'function') {
    return address.toString();
  }

  // Otherwise convert to string
  return String(address);
}

/**
 * Sends a request to the Shyft API
 * @param method - The RPC method to call
 * @param params - The parameters to send with the request
 * @returns The response from the API
 */
export async function callShyftApi(method: string, params: any[] = []) {
  try {
    // Carefully sanitize params to prevent circular references while preserving structure
    const sanitizedParams = params.map(param => {
      if (param === null || param === undefined) {
        return param;
      }

      // If param is an object with a single address property, handle it specially
      if (typeof param === 'object' && !Array.isArray(param)) {
        const result: Record<string, any> = {};
        for (const key in param) {
          if (Object.prototype.hasOwnProperty.call(param, key)) {
            result[key] = sanitizeForStringify(param[key]);
          }
        }
        return result;
      }

      return sanitizeForStringify(param);
    });

    const response = await fetch(SHYFT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method,
        params: sanitizedParams,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(`Shyft API error: ${data.error.message}`);
    }

    return data.result;
  } catch (error) {
    console.error(`Error in Shyft API call (${method}):`, error);
    throw error;
  }
}

/**
 * Gets the current block height from the Shyft API with caching
 * @returns The current block height
 */
export async function getBlockHeight() {
  const now = Date.now();

  // Return cached value if still valid
  if (blockHeightCache.value !== null && now - blockHeightCache.timestamp < CACHE_TIMEOUT) {
    return blockHeightCache.value;
  }

  try {
    const result = await callShyftApi('getBlockHeight');

    // Update cache
    blockHeightCache = {
      timestamp: now,
      value: result
    };

    return result;
  } catch (error) {
    // On error, invalidate cache if it's old
    if (now - blockHeightCache.timestamp > CACHE_TIMEOUT) {
      blockHeightCache.value = null;
    }
    throw error;
  }
}

/**
 * Gets account information from the Shyft API
 * @param address - The account address to query
 * @param encoding - The encoding to use for the response
 * @returns The account information
 */
export async function getAccountInfo(address: any, encoding = 'jsonParsed') {
  // Ensure address is a string
  const addressStr = ensureAddressString(address);
  return callShyftApi('getAccountInfo', [addressStr, { encoding }]);
}

/**
 * Gets the balance of an account from the Shyft API
 * @param address - The account address to query
 * @returns The account balance in lamports
 */
export async function getBalance(address: any) {
  // Ensure address is a string
  const addressStr = ensureAddressString(address);

  // Try up to 3 times with exponential backoff
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      // Create a direct connection with 'finalized' commitment to ensure latest data
      const connection = new Connection(
        SHYFT_API_URL,
        'finalized'  // Use highest commitment level for most accurate data
      );

      // Get balance directly from RPC with skipPreflight=false and preflightCommitment='finalized'
      const balance = await connection.getBalance(
        new PublicKey(addressStr),
        'finalized'  // Specify commitment here as well
      );

      return balance;
    } catch (error) {
      if (attempt < 2) {  // Don't wait after the last attempt
        // Exponential backoff: 500ms, then 1500ms
        await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(3, attempt)));
      }
    }
  }

  // If all attempts failed, try one last time with the regular endpoint
  try {
    const result = await callShyftApi('getBalance', [addressStr]);

    // Handle different result types
    if (typeof result === 'string') {
      return parseFloat(result) || 0;
    }

    // Some return it as an object with a value property
    if (result && typeof result === 'object' && 'value' in result) {
      return result.value || 0;
    }

    // Default case: return as-is or 0
    return result || 0;
  } catch (finalError) {
    console.error(`All attempts to get balance for ${addressStr} failed`, finalError);
    return 0; // Return 0 as a safe default rather than throwing
  }
}

/**
 * Gets token balances for an account from the Shyft API
 * @param address - The account address to query
 * @param forceRefresh - Force a fresh fetch bypassing any caching
 * @returns The token balances for the account
 */
export async function getTokenAccounts(address: any, forceRefresh = false) {
  try {
    // Ensure address is a string
    const addressStr = ensureAddressString(address);

    // Use string address for cache key
    const cacheKey = `tokenAccounts_${addressStr}`;
    const now = Date.now();

    // Return cached value if still valid and not forcing refresh
    if (!forceRefresh &&
      tokenBalancesCache[cacheKey] &&
      now - tokenBalancesCache[cacheKey].timestamp < CACHE_TIMEOUT) {
      return tokenBalancesCache[cacheKey].value;
    }

    const programId = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
    const result = await callShyftApi('getTokenAccountsByOwner', [
      addressStr,
      { programId },
      { encoding: 'jsonParsed' }
    ]);

    // Update cache
    tokenBalancesCache[cacheKey] = {
      timestamp: now,
      value: result
    };

    return result;
  } catch (error) {
    console.error(`Error fetching token accounts:`, error);
    // On error, return empty result with correct structure
    return { value: [] };
  }
}

/**
 * Gets recent transactions for an account from the Shyft API
 * @param address - The account address to query
 * @param limit - The maximum number of transactions to return
 * @param before - Optional signature to fetch transactions before
 * @param forceRefresh - Force a fresh fetch bypassing any caching
 * @returns The recent transactions for the account
 */
export async function getTransactions(address: any, limit = 10, before?: string, forceRefresh = false) {
  // Ensure address is a string
  const addressStr = ensureAddressString(address);

  const options: any = { limit };

  if (before) {
    options.before = before;
  }

  // Add a cache-busting random parameter to force fresh results when needed
  if (forceRefresh) {
    options._nocache = Date.now();
  }

  return callShyftApi('getSignaturesForAddress', [addressStr, options]);
}

/**
 * Gets the token account info for a specific mint
 * @param ownerAddress - The owner address
 * @param tokenMint - The token mint address
 * @param forceRefresh - Force a fresh fetch bypassing any caching
 * @returns The token account info
 */
export async function getTokenAccountByMint(ownerAddress: any, tokenMint: any, forceRefresh = false) {
  // Ensure addresses are strings
  const ownerAddressStr = ensureAddressString(ownerAddress);
  const tokenMintStr = ensureAddressString(tokenMint);

  // Get token accounts with force refresh option
  const tokenAccounts = await getTokenAccounts(ownerAddressStr, forceRefresh);

  if (!tokenAccounts || !tokenAccounts.value || !tokenAccounts.value.length) {
    return null;
  }

  // Find the token account for the specified mint
  for (const account of tokenAccounts.value) {
    try {
      const parsedData = account.account.data.parsed;
      if (
        parsedData &&
        parsedData.info &&
        parsedData.info.mint &&
        parsedData.info.mint === tokenMintStr
      ) {
        return account;
      }
    } catch (err) {
      console.error("Error parsing token account data:", err);
    }
  }

  return null;
}

/**
 * Gets all the token balances for an account
 * @param address - The account address to query
 * @returns Object mapping token mints to their balances
 */
export async function getTokenBalances(address: any) {
  try {
    // Ensure address is a string
    const addressStr = ensureAddressString(address);

    const tokenAccounts = await getTokenAccounts(addressStr);

    if (!tokenAccounts || !tokenAccounts.value || !tokenAccounts.value.length) {
      return {};
    }

    const balances: Record<string, string> = {};

    for (const account of tokenAccounts.value) {
      try {
        const parsedData = account.account.data.parsed;
        if (parsedData && parsedData.info) {
          const { mint, tokenAmount } = parsedData.info;
          balances[mint] = tokenAmount.uiAmountString || '0';
        }
      } catch (err) {
        console.error("Error parsing token balance:", err);
      }
    }

    return balances;
  } catch (error) {
    console.error("Failed to fetch token balances:", error);
    return {};
  }
}

/**
 * Gets a specific token balance for an account
 * @param address - The account address
 * @param tokenMint - The token mint address
 * @param forceRefresh - Force a fresh fetch bypassing any caching
 * @returns The token balance or 0 if not found
 */
export async function getTokenBalance(address: any, tokenMint: any, forceRefresh = false) {
  try {
    // Ensure addresses are strings
    const addressStr = ensureAddressString(address);
    const tokenMintStr = ensureAddressString(tokenMint);

    // Generate a cache key for this specific token and address pair
    const cacheKey = `tokenBalance_${addressStr}_${tokenMintStr}`;
    const now = Date.now();

    // Use a shorter cache timeout after transactions
    const effectiveCacheTimeout = forceRefresh ? POST_TX_CACHE_TIMEOUT : CACHE_TIMEOUT;

    // Return cached value if still valid and force refresh is not requested
    if (!forceRefresh &&
      tokenBalancesCache[cacheKey] &&
      now - tokenBalancesCache[cacheKey].timestamp < effectiveCacheTimeout) {
      return tokenBalancesCache[cacheKey].value;
    }

    // Try multiple methods to get the token balance for reliability
    let balance = null;
    let errors = [];

    // Method 1: Try getting the token account directly first
    try {
      const tokenAccount = await getTokenAccountByMint(addressStr, tokenMintStr, forceRefresh);

      if (tokenAccount) {
        try {
          const tokenAmount = tokenAccount.account.data.parsed.info.tokenAmount;
          balance = tokenAmount.uiAmountString || '0';
        } catch (parseErr) {
          errors.push(`Method 1 parse error: ${parseErr}`);
        }
      }
    } catch (method1Error) {
      errors.push(`Method 1 failed: ${method1Error}`);
    }

    // Method 2: If method 1 failed or returned 0, try getting all token balances
    if (!balance || balance === '0') {
      try {
        const allBalances = await getTokenBalances(addressStr);
        if (allBalances[tokenMintStr]) {
          balance = allBalances[tokenMintStr];
        }
      } catch (method2Error) {
        errors.push(`Method 2 failed: ${method2Error}`);
      }
    }

    // Method 3: If previous methods failed or returned 0, try a direct RPC connection
    if (!balance || balance === '0') {
      try {
        // Create a direct connection with high commitment level
        const connection = new Connection(
          SHYFT_API_URL,
          'confirmed'
        );

        // Get token accounts directly 
        const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
          new PublicKey(addressStr),
          { mint: new PublicKey(tokenMintStr) }
        );

        if (tokenAccounts.value.length > 0) {
          const account = tokenAccounts.value[0];
          const accountData = account.account.data;
          const tokenAmount = accountData.parsed.info.tokenAmount;
          balance = tokenAmount.uiAmountString || '0';
        }
      } catch (method3Error) {
        errors.push(`Method 3 failed: ${method3Error}`);
      }
    }

    // If we found a balance through any method
    if (balance) {
      // Update cache with new balance
      tokenBalancesCache[cacheKey] = {
        timestamp: now,
        value: balance
      };

      // Broadcast a balance update event
      try {
        const event = new CustomEvent('ekko:token_balance_update', {
          detail: {
            address: addressStr,
            mint: tokenMintStr,
            balance: balance,
            timestamp: now
          }
        });
        window.dispatchEvent(event);
      } catch (eventError) {
        console.error('Failed to dispatch token balance update event:', eventError);
      }

      return balance;
    }

    // If all methods failed, log errors but return cached value if available
    console.error(`All methods failed to get token balance for ${tokenMintStr}:`, errors);

    if (tokenBalancesCache[cacheKey]) {
      return tokenBalancesCache[cacheKey].value;
    }

    // Update cache with zero balance as last resort
    tokenBalancesCache[cacheKey] = {
      timestamp: now,
      value: '0'
    };
    return '0';
  } catch (error) {
    console.error(`Failed to fetch balance for token ${tokenMint}:`, error);
    return '0';
  }
}

// Export sendAndConfirmTransaction function for use in our tradeEngine
export async function sendAndConfirmTransaction(
  transaction: Transaction,
  signers: Keypair[]
): Promise<string> {
  const connection = new Connection(SOLANA_RPC_URL, 'confirmed');

  // Set recent blockhash
  transaction.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
  transaction.feePayer = signers[0].publicKey;

  // Sign and send the transaction
  const signature = await solanaSignAndConfirmTransaction(
    connection,
    transaction,
    signers,
    { commitment: 'confirmed' }
  );

  return signature;
}

// Platform fee functionality completely removed for maximum speed